'use client';

import React from 'react';
import type { Breadcrumb } from '@benzinga/content-manager';
import { Breadcrumbs, EdgeBadge } from '@benzinga/core-ui';
import { ShareButtons } from '@benzinga/ui';
import { mergeClassNames } from '../../_utils/mergeClassNames';
import { LayoutPageHeader } from './LayoutPageHeader';
import { PageHeaderTabs, type PageHeaderTab } from './PageHeaderTabs';

export type HeroPageHeaderTab = PageHeaderTab;

export interface HeroPageHeaderProps {
  actions?: React.ReactNode;
  bottomContent?: React.ReactNode;
  breadcrumbs: Breadcrumb[];
  buttonLocation: string;
  canonical: string;
  className?: string;
  description?: string;
  descriptionFooter?: React.ReactNode;
  leadingContent?: React.ReactNode;
  metaContent?: React.ReactNode;
  navAriaLabel?: string;
  showEdgeBadge?: boolean;
  subtitle?: string;
  tabs?: HeroPageHeaderTab[];
  title: string;
}

export function HeroPageHeader({
  actions,
  bottomContent,
  breadcrumbs,
  buttonLocation,
  canonical,
  className,
  description,
  descriptionFooter,
  leadingContent,
  metaContent,
  navAriaLabel = 'Page sections',
  showEdgeBadge = false,
  subtitle,
  tabs = [],
  title,
}: HeroPageHeaderProps) {
  const shareButtons = (
    <div className="header-actions max-md:mt-0 max-md:shrink-0 max-md:pt-0">
      <ShareButtons
        buttonLocation={buttonLocation}
        showButtons={['linkedin', 'twitter', 'facebook', 'bluesky', 'email', 'copy']}
        title={title}
        url={canonical}
        buttonStyle={{ borderRadius: 4 as number }}
        emailButtonStyle={{
          bgStyle: { fill: '#17315d' } as React.CSSProperties,
          iconFillColor: '#3F83F8',
        }}
      />
    </div>
  );

  return (
    <LayoutPageHeader className={mergeClassNames('w-full', className)}>
      <div className="layout-page-header-meta mb-4">
        <Breadcrumbs
          className="calendar-header-crumbs layout-page-header-meta__crumbs"
          data={breadcrumbs}
          separator="/"
        />
        {metaContent}
      </div>
      <div className="layout-page-header-intro mb-2 max-md:mb-3 [&_.header-description]:text-pretty [&_.header-description]:leading-[1.55] max-md:[&_.header-description]:mb-0 max-md:[&_.header-description]:max-w-[36ch] max-md:[&_.header-description]:text-sm max-md:[&_.header-description]:leading-normal [&_.header-title]:mb-2.5 [&_.header-title]:text-balance max-md:[&_.header-title]:mb-3 max-md:[&_.header-title]:text-[22px] max-md:[&_.header-title]:normal-case max-md:[&_.header-title]:leading-tight max-md:[&_.header-title]:tracking-wide">
        <h1 className="header-title text-white">
          <span>{title}</span>
          {showEdgeBadge && <span className="layout-page-header-intro__edge">{EdgeBadge}</span>}
        </h1>
        {subtitle && <p className="header-subtitle text-white">{subtitle}</p>}
        {description && <p className="header-description">{description}</p>}
        {descriptionFooter}
      </div>
      <div className="calendar-header-bottom-bar">
        {tabs.length > 0 || leadingContent || bottomContent ? (
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
            {leadingContent}
            <PageHeaderTabs navAriaLabel={navAriaLabel} tabs={tabs} />
            {bottomContent ? <div className="w-full min-w-0">{bottomContent}</div> : null}
          </div>
        ) : null}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
          {shareButtons}
        </div>
      </div>
    </LayoutPageHeader>
  );
}
